import { useState, useEffect, useRef, useCallback } from 'react';
import proxy from '../api/axios';
import { DISCUSSION_TAGS } from '../components/problems/discussion/discussionUtils';

export { DISCUSSION_TAGS };

export async function fetchDiscussionPost(postId) {
  const { data } = await proxy.get(`/problems/discussions/post/${postId}`);
  return data;
}

export async function createDiscussionPost(slug, payload) {
  const { data } = await proxy.post(`/problems/${slug}/discussions`, payload);
  return data;
}

export async function postDiscussionComment(postId, body, parentId = null) {
  const { data } = await proxy.post(`/problems/discussions/post/${postId}/comments`, {
    body,
    parentId,
  });
  return data;
}

export async function togglePostUpvote(postId) {
  const { data } = await proxy.post(`/problems/discussions/post/${postId}/upvote`);
  return data;
}

export async function toggleCommentUpvote(commentId) {
  const { data } = await proxy.post(`/problems/discussions/comment/${commentId}/upvote`);
  return data;
}

export async function deleteDiscussionPost(postId) {
  const { data } = await proxy.delete(`/problems/discussions/post/${postId}`);
  return data;
}

export async function deleteDiscussionComment(commentId) {
  const { data } = await proxy.delete(`/problems/discussions/comment/${commentId}`);
  return data;
}

/**
 * Unified discussion state: list cache, per-thread cache, optimistic mutations.
 */
export function useProblemDiscussions({ slug, isLoggedIn, active, enabled = true }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [sort, setSort] = useState('newest');
  const [tag, setTag] = useState('all');
  const [search, setSearch] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [selectedPostId, setSelectedPostId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const threadCacheRef = useRef(new Map());
  const listCacheKeyRef = useRef(null);
  const listRequestRef = useRef(0);
  const threadRequestRef = useRef(0);

  const listCacheKey = slug ? `${slug}:${sort}:${tag}` : null;

  const fetchList = useCallback(
    async ({ silent = false } = {}) => {
      if (!slug) return;

      const requestId = ++listRequestRef.current;
      if (!silent) {
        setListLoading(true);
        setListError(null);
      }

      try {
        const { data } = await proxy.get(`/problems/${slug}/discussions`, {
          params: { sort, tag, page: 1, limit: 30 },
        });
        if (requestId !== listRequestRef.current) return;

        const nextItems = Array.isArray(data?.items) ? data.items : [];
        setItems(nextItems);
        setPagination(data?.pagination || { page: 1, totalPages: 1, total: 0 });
        listCacheKeyRef.current = listCacheKey;
      } catch (err) {
        if (requestId !== listRequestRef.current) return;
        if (!silent) {
          setListError(err?.response?.data?.message || 'Could not load discussions.');
        }
      } finally {
        if (requestId === listRequestRef.current && !silent) {
          setListLoading(false);
        }
      }
    },
    [slug, sort, tag, listCacheKey],
  );

  const loadThread = useCallback(
    async (postId, { silent = false } = {}) => {
      if (!postId) return;

      const cached = threadCacheRef.current.get(postId);
      if (cached) {
        setThread(cached);
        setThreadError(null);
      }

      const requestId = ++threadRequestRef.current;
      if (!cached && !silent) {
        setThreadLoading(true);
        setThreadError(null);
      }

      try {
        const data = await fetchDiscussionPost(postId);
        if (requestId !== threadRequestRef.current) return;

        threadCacheRef.current.set(postId, data);
        setThread(data);
        setThreadError(null);

        setItems((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  upvoteCount: data.post.upvoteCount,
                  commentCount: data.post.commentCount,
                  upvoted: data.post.upvoted,
                }
              : p,
          ),
        );
      } catch (err) {
        if (requestId !== threadRequestRef.current) return;
        if (!cached) {
          setThreadError(err?.response?.data?.message || 'Could not load this topic.');
        }
      } finally {
        if (requestId === threadRequestRef.current) {
          setThreadLoading(false);
        }
      }
    },
    [],
  );

  const selectPost = useCallback(
    (postId) => {
      setSelectedPostId(postId);
      loadThread(postId);
    },
    [loadThread],
  );

  const clearSelection = useCallback(() => {
    setSelectedPostId(null);
    setThread(null);
    setThreadError(null);
    threadRequestRef.current += 1;
  }, []);

  const patchListItem = useCallback((postId, patch) => {
    setItems((prev) => prev.map((p) => (p.id === postId ? { ...p, ...patch } : p)));
  }, []);

  const prependPost = useCallback((post) => {
    setItems((prev) => [post, ...prev.filter((p) => p.id !== post.id)]);
    threadCacheRef.current.set(post.id, { post, comments: [], tags: DISCUSSION_TAGS });
  }, []);

  const removePostFromList = useCallback((postId) => {
    setItems((prev) => prev.filter((p) => p.id !== postId));
    threadCacheRef.current.delete(postId);
  }, []);

  const patchThreadPost = useCallback((patch) => {
    setThread((prev) => {
      if (!prev?.post) return prev;
      const next = { ...prev, post: { ...prev.post, ...patch } };
      if (selectedPostId) threadCacheRef.current.set(selectedPostId, next);
      return next;
    });
  }, [selectedPostId]);

  const patchThreadComments = useCallback(
    (updater) => {
      setThread((prev) => {
        if (!prev) return prev;
        const comments = typeof updater === 'function' ? updater(prev.comments || []) : updater;
        const next = { ...prev, comments };
        if (selectedPostId) threadCacheRef.current.set(selectedPostId, next);
        return next;
      });
    },
    [selectedPostId],
  );

  useEffect(() => {
    if (!enabled || !active || !slug) return;
    if (listCacheKeyRef.current === listCacheKey && items.length) return;
    fetchList();
  }, [enabled, active, slug, sort, tag, fetchList, listCacheKey, items.length]);

  useEffect(() => {
    listCacheKeyRef.current = null;
    threadCacheRef.current.clear();
    setItems([]);
    setThread(null);
    setSelectedPostId(null);
    setListError(null);
    setThreadError(null);
  }, [slug]);

  const toggleExpand = useCallback(
    (postId) => {
      if (selectedPostId === postId) {
        clearSelection();
      } else {
        selectPost(postId);
      }
    },
    [selectedPostId, clearSelection, selectPost],
  );

  const filteredItems = search.trim()
    ? items.filter((p) => {
        const q = search.trim().toLowerCase();
        return (
          p.title?.toLowerCase().includes(q) ||
          p.tags?.some((t) => t.toLowerCase().includes(q)) ||
          p.author?.name?.toLowerCase().includes(q) ||
          p.author?.username?.toLowerCase().includes(q)
        );
      })
    : items;

  return {
    items: filteredItems,
    pagination,
    sort,
    setSort,
    tag,
    setTag,
    search,
    setSearch,
    listLoading,
    listError,
    selectedPostId,
    thread,
    threadLoading,
    threadError,
    selectPost,
    clearSelection,
    toggleExpand,
    fetchList,
    loadThread,
    patchListItem,
    prependPost,
    removePostFromList,
    patchThreadPost,
    patchThreadComments,
  };
};
